import { useState, useEffect, useLayoutEffect, useRef } from "react";
import api from "../../api/client";

/**
 * CompanyAssignmentPopover — Compact inline popover for assigning/editing the
 * Company on a For Dispatch job order row.
 *
 * Props:
 *   anchorRect — DOMRect from getBoundingClientRect() of the clicked cell
 *   record     — { id, company_id, company_name }
 *   onSave     — (updatedRecord) => void — called after a successful save
 *   onClose    — () => void
 */
export default function CompanyAssignmentPopover({ anchorRect, record, onSave, onClose }) {
  const popoverRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/companies/active").then(({ data }) => setCompanies(data || [])).catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Position the popover anchored directly below the clicked cell. We measure
  // the actual rendered size and only flip above / clamp when it would truly
  // overflow the viewport — so it stays at the click location instead of
  // jumping up from a hardcoded height estimate.
  const [pos, setPos] = useState(() =>
    anchorRect ? { top: anchorRect.bottom + 4, left: anchorRect.left } : { top: 0, left: 0 }
  );

  useLayoutEffect(() => {
    if (!anchorRect || !popoverRef.current) return;
    const { width, height } = popoverRef.current.getBoundingClientRect();
    let top = anchorRect.bottom + 4;
    let left = anchorRect.left;
    if (left + width > window.innerWidth) left = window.innerWidth - width - 8;
    if (top + height > window.innerHeight) top = anchorRect.top - height - 4;
    if (left < 4) left = 4;
    if (top < 4) top = 4;
    setPos({ top, left });
    // Re-measure once the async company list loads and grows the popover.
  }, [anchorRect, companies.length]);

  const handleSelect = async (companyId) => {
    setError("");
    setSaving(true);
    try {
      const { data: updated } = await api.put(`/job-orders/${record.id}/company`, {
        company_id: companyId,
      });
      onSave(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save company.");
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50" onClick={onClose} />

      {/* Popover */}
      <div
        ref={popoverRef}
        className="bg-white rounded-lg shadow-xl border border-gray-200 w-[230px] z-50 flex flex-col max-h-[300px]"
        style={{ position: "fixed", top: pos.top, left: pos.left }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</p>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Options */}
        <div className="overflow-y-auto flex-1">
          {record.company_id && (
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSelect(null)}
              className="w-full text-left px-4 py-2 text-sm text-gray-500 italic hover:bg-gray-50 border-b disabled:opacity-50"
            >
              Clear
            </button>
          )}
          {companies.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">No active companies</div>
          ) : (
            companies.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={saving}
                onClick={() => handleSelect(c.id)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 disabled:opacity-50 ${
                  c.id === record.company_id ? "bg-blue-600 text-white hover:bg-blue-700" : "text-gray-800"
                }`}
                title={c.name}
              >
                {c.prefix || c.name}
              </button>
            ))
          )}
        </div>

        {error && <p className="text-xs text-red-600 px-4 py-2 flex-shrink-0">{error}</p>}
      </div>
    </>
  );
}
